import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { GraduationCap, CheckCircle, Clock, Calendar } from 'lucide-react';
import EmployeePortalCrudShell from './EmployeePortalCrudShell';
import { statusBadge, defaultStats, sessionIncludesEmployee } from './employeePortalShared';
import { formatPortalDate, formatPortalMoney, resolveOptionLabel } from './employeePortalSaudiFormat';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'trainingPage';
const API = '/api/hr/training';

const TRAINING_STATUS = [
  { value: 'planifie', labelKey: 'train_planned' },
  { value: 'en_cours', labelKey: 'train_in_progress' },
  { value: 'termine', labelKey: 'train_done' },
  { value: 'annule', labelKey: 'train_cancelled' },
];

const TRAINING_CATEGORIES = [
  { value: 'hrdf', labelKey: 'cat_hrdf' },
  { value: 'saudization', labelKey: 'cat_saudization' },
  { value: 'bilingual', labelKey: 'cat_bilingual' },
  { value: 'technical', labelKey: 'cat_technical' },
  { value: 'leadership', labelKey: 'cat_leadership' },
  { value: 'compliance', labelKey: 'cat_compliance' },
  { value: 'other', labelKey: 'cat_other' },
];

const EMPTY = {
  title: '', trainer: '', provider: '', category: '', startDate: '', endDate: '',
  location: '', status: '', cost: '', hrdfSupported: '', language: '', certificate: '', description: '',
};

function formatLanguage(lang, pt) {
  if (!lang) return '';
  const map = {
    ar: pt('lang_ar'),
    en: pt('lang_en'),
    bilingual: pt('lang_bilingual'),
    'ar-en': pt('lang_bilingual'),
  };
  return map[lang] || lang;
}

function formatHrdfLabel(supported, pt) {
  if (supported === true || supported === 1 || supported === '1') return pt('hrdf_yes');
  if (supported === false || supported === 0 || supported === '0') return pt('hrdf_no');
  return '';
}

export default function EmployeePortalTrainingPage() {
  const { i18n, t } = useTranslation();
  const pt = (key) => t(`modules.employee_portal.pages.${NS}.${key}`, { defaultValue: '' });
  const pl = (key) => t(`modules.employee_portal.pages.common.${key}`, { defaultValue: '' }) || pt(key);
  const { employeeId } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(() => (employeeId ? { employee_id: employeeId } : {}), [employeeId]);
  const filterRecords = useCallback(
    (record) => sessionIncludesEmployee(record, employeeId),
    [employeeId],
  );

  const renderDate = (key) => (r) => formatPortalDate(r[key], i18n.language);
  const renderCost = (r) => {
    const cost = r.cost ?? r.budget ?? r.trainingCost;
    return cost != null && cost !== '' ? formatPortalMoney(cost) : '—';
  };
  const renderCategory = (r, _p, _t, labelFn) => resolveOptionLabel(r.category, TRAINING_CATEGORIES, labelFn);
  const renderStatus = (r, _p, _t, labelFn) => statusBadge(r.status, labelFn, TRAINING_STATUS);

  const mapRecordToForm = (r) => {
    const cost = r.cost ?? r.budget ?? r.trainingCost;
    const supported = r.hrdfSupported ?? r.hrdf_support ?? r.hrdfSupport;
    return {
      title: r.title || r.sessionTitle || '',
      trainer: r.trainer || r.trainerName || '',
      provider: r.provider || r.trainingProvider || r.trainer || '',
      category: resolveOptionLabel(r.category, TRAINING_CATEGORIES, pl),
      startDate: formatPortalDate(r.startDate, i18n.language),
      endDate: formatPortalDate(r.endDate, i18n.language),
      location: r.location || '',
      status: resolveOptionLabel(r.status, TRAINING_STATUS, pl) || r.status || '',
      cost: cost != null && cost !== '' ? formatPortalMoney(cost) : '',
      hrdfSupported: formatHrdfLabel(supported, pt),
      language: formatLanguage(r.language || r.trainingLanguage, pt),
      certificate: r.certificate || r.certificateUrl || '',
      description: r.description || '',
    };
  };

  return (
    <EmployeePortalCrudShell
      ns={NS}
      icon={GraduationCap}
      apiPath={API}
      apiQueryParams={apiQueryParams}
      requireEmployeeScope
      filterRecords={employeeId ? filterRecords : undefined}
      allowCreate={false}
      allowEdit={false}
      allowDelete={false}
      gradient="from-indigo-500 to-indigo-700"
      emptyForm={EMPTY}
      mapRecordToForm={mapRecordToForm}
      computeStats={(records) => defaultStats(records, {
        totalIcon: GraduationCap,
        fields: [
          { titleKey: 'stat_planned', field: 'status', value: 'planifie', icon: Calendar, color: 'from-blue-500 to-blue-600' },
          { titleKey: 'stat_in_progress', field: 'status', value: 'en_cours', icon: Clock, color: 'from-amber-500 to-amber-600' },
          { titleKey: 'stat_done', field: 'status', value: 'termine', icon: CheckCircle, color: 'from-green-500 to-green-600' },
        ],
      })}
      columns={[
        { key: 'title', labelKey: 'col_title' },
        { key: 'provider', labelKey: 'col_provider' },
        { key: 'category', labelKey: 'col_category', render: renderCategory },
        { key: 'startDate', labelKey: 'col_start', render: renderDate('startDate') },
        { key: 'cost', labelKey: 'col_cost', render: renderCost },
        { key: 'status', labelKey: 'col_status', render: renderStatus },
      ]}
      formSections={[
        {
          id: 'overview',
          titleKey: 'section_overview',
          fields: [
            { key: 'title', labelKey: 'col_title', readOnly: true },
            { key: 'provider', labelKey: 'col_provider', readOnly: true },
            { key: 'trainer', labelKey: 'col_trainer', readOnly: true },
            { key: 'category', labelKey: 'col_category', readOnly: true },
            { key: 'status', labelKey: 'col_status', readOnly: true },
          ],
        },
        {
          id: 'schedule',
          titleKey: 'section_schedule',
          fields: [
            { key: 'startDate', labelKey: 'col_start', readOnly: true },
            { key: 'endDate', labelKey: 'col_end', readOnly: true },
            { key: 'location', labelKey: 'col_location', readOnly: true },
            { key: 'cost', labelKey: 'col_cost', readOnly: true },
            { key: 'hrdfSupported', labelKey: 'field_hrdf', readOnly: true },
            { key: 'language', labelKey: 'field_language', readOnly: true },
          ],
        },
        {
          id: 'details',
          titleKey: 'section_details',
          fields: [
            { key: 'certificate', labelKey: 'field_certificate', readOnly: true },
            { key: 'description', labelKey: 'field_description', type: 'textarea', readOnly: true },
          ],
        },
      ]}
    />
  );
}
